import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';
import PersonIcon from '@mui/icons-material/Person';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import ApiKeyIcon from '@mui/icons-material/VpnKey';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import UsersIcon from '@mui/icons-material/People';
import ProjectsIcon from '@mui/icons-material/FolderSpecial';
import FilesIcon from '@mui/icons-material/CloudDownload';
import IntegrityIcon from '@mui/icons-material/HealthAndSafety';
import BackupIcon from '@mui/icons-material/Backup';
import OrganizationIcon from '@mui/icons-material/Business';
import SystemStatusIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorageIcon from '@mui/icons-material/Storage';
import StarIcon from '@mui/icons-material/Star';
import LogsIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import CalibrationIcon from '@mui/icons-material/Tune';
import TimelineIcon from '@mui/icons-material/Timeline';
import WeatherIcon from '@mui/icons-material/WbSunny';
import { useAuth } from '../../contexts/AuthContext';
import ObservatoryDomeIcon from '../icons/ObservatoryDomeIcon';
import ObservatoryIcon from '../icons/ObservatoryIcon';

const allCommands = [
  // Operator views
  { text: 'Operations Dashboard', icon: <DashboardIcon />, path: 'operations', category: 'Operator', keywords: 'home main monitor' },
  { text: 'Schedule', icon: <TimelineIcon />, path: 'schedule', category: 'Operator', keywords: 'schedule tonight gantt timeline' },
  { text: 'Weather', icon: <WeatherIcon />, path: 'weather', category: 'Operator', keywords: 'conditions cloud wind humidity' },
  { text: 'Observatory', icon: <ObservatoryDomeIcon />, path: 'observatory', category: 'Operator', keywords: 'health status diagnostics system equipment' },
  // User pages
  { text: 'Submit Observation', icon: <AddIcon />, path: '/observations/new', category: 'User', keywords: 'new create target' },
  { text: 'Search Observations', icon: <SearchIcon />, path: '/observations/search', category: 'User', keywords: 'find query' },
  { text: 'My Observations', icon: <ListIcon />, path: '/observations', category: 'User', keywords: 'list' },
  { text: 'My Files', icon: <FilesIcon />, path: '/files', category: 'User', keywords: 'fits images data' },
  { text: 'My Projects', icon: <ProjectsIcon />, path: '/projects', category: 'User', keywords: 'project' },
  { text: 'Target Library', icon: <StarIcon />, path: '/library', category: 'User', keywords: 'catalog targets' },
  { text: 'Frame Library', icon: <CalibrationIcon />, path: '/calibration/library', category: 'User', keywords: 'calibration darks flats bias' },
  { text: 'Activity History', icon: <HistoryIcon />, path: '/activity-history', category: 'User', keywords: 'log' },
  { text: 'Profile', icon: <PersonIcon />, path: '/profile', category: 'User', keywords: 'account settings' },
  { text: 'My Usage', icon: <TrendingUpIcon />, path: '/my-usage', category: 'User', keywords: 'billing time' },
  // Admin pages
  { text: 'Admin Dashboard', icon: <AdminIcon />, path: '/admin/dashboard', category: 'Admin', keywords: 'overview stats', admin: true },
  { text: 'Observatory Management', icon: <ObservatoryIcon />, path: '/admin/observatories', category: 'Admin', keywords: 'manage telescopes', admin: true },
  { text: 'User Management', icon: <UsersIcon />, path: '/admin/users', category: 'Admin', keywords: 'accounts roles', admin: true },
  { text: 'Organization Management', icon: <OrganizationIcon />, path: '/admin/organizations', category: 'Admin', keywords: 'org institution', admin: true },
  { text: 'API Keys', icon: <ApiKeyIcon />, path: '/admin/api-keys', category: 'Admin', keywords: 'tokens authentication', admin: true },
  { text: 'System Settings', icon: <SettingsIcon />, path: '/admin/settings', category: 'Admin', keywords: 'configuration', admin: true },
  { text: 'System Status', icon: <SystemStatusIcon />, path: '/admin/status', category: 'Admin', keywords: 'health containers', admin: true },
  { text: 'Database Integrity', icon: <IntegrityIcon />, path: '/admin/integrity', category: 'Admin', keywords: 'check consistency', admin: true },
  { text: 'Backup Management', icon: <BackupIcon />, path: '/admin/backups', category: 'Admin', keywords: 'restore snapshot', admin: true },
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics', category: 'Admin', keywords: 'stats metrics', admin: true },
  { text: 'Log Viewer', icon: <LogsIcon />, path: '/admin/logs', category: 'Admin', keywords: 'errors debug', admin: true },
  { text: 'Log Settings', icon: <StorageIcon />, path: '/admin/logs/settings', category: 'Admin', keywords: 'retention', admin: true },
  // Reporting
  { text: 'Usage Summary', icon: <TrendingUpIcon />, path: '/admin/billing/dashboard', category: 'Reporting', keywords: 'billing dashboard usage', admin: true },
  { text: 'Usage Explorer', icon: <SearchIcon />, path: '/admin/billing/explorer', category: 'Reporting', keywords: 'detailed', admin: true },
];

const CommandPalette = ({ open, onClose, observatoryId }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { user } = useAuth();

  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';

  const filtered = useMemo(() => {
    const commands = allCommands.filter(cmd => !cmd.admin || isAdmin);
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(cmd =>
      cmd.text.toLowerCase().includes(q) ||
      cmd.keywords.toLowerCase().includes(q) ||
      cmd.category.toLowerCase().includes(q)
    );
  }, [query, isAdmin]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = (cmd) => {
    // Operator-relative paths
    if (!cmd.path.startsWith('/') && observatoryId) {
      navigate(`/observatory/${observatoryId}/${cmd.path}`);
    } else {
      navigate(cmd.path);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      handleSelect(filtered[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  let lastCategory = '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          mt: -10,
        },
      }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.6)' } } }}
    >
      <TextField
        inputRef={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a command or page name..."
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
            </InputAdornment>
          ),
          sx: {
            fontSize: '0.95rem',
            '& fieldset': { border: 'none' },
          },
        }}
        sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      />
      <List sx={{ maxHeight: 400, overflow: 'auto', py: 0.5 }}>
        {filtered.map((cmd, index) => {
          const showCategory = cmd.category !== lastCategory;
          lastCategory = cmd.category;
          return (
            <React.Fragment key={cmd.path}>
              {showCategory && (
                <Typography
                  variant="caption"
                  sx={{ px: 2, py: 0.5, display: 'block', color: 'rgba(255,255,255,0.35)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  {cmd.category}
                </Typography>
              )}
              <ListItem disablePadding>
                <ListItemButton
                  selected={index === selectedIndex}
                  onClick={() => handleSelect(cmd)}
                  sx={{
                    py: 0.5,
                    px: 2,
                    '&.Mui-selected': { backgroundColor: 'rgba(144,202,249,0.08)' },
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'rgba(255,255,255,0.5)' }}>
                    {cmd.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={cmd.text}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.6rem' }}>
                    {cmd.category}
                  </Typography>
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          );
        })}
        {filtered.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No matching commands</Typography>
          </Box>
        )}
      </List>
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', px: 2, py: 0.75, display: 'flex', gap: 2 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem' }}>
          <kbd style={{ padding: '1px 4px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>↑↓</kbd> Navigate
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem' }}>
          <kbd style={{ padding: '1px 4px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>Enter</kbd> Select
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem' }}>
          <kbd style={{ padding: '1px 4px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>Esc</kbd> Close
        </Typography>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
